import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';
import { ERAS } from '../game/cosmos.js';

const MAX_UNIVERSES = 12;
const MAX_SNAPSHOT_STARS = 450;
const MAX_THUMB_LENGTH = 120_000;

/**
 * Persistent gallery of created universes (newest first).
 * Same strict-sanitization philosophy as ScoreStore.
 */
export default class UniverseStore {
  constructor() {
    this.universes = this._load();
  }

  _load() {
    const raw = storageGet(STORAGE.UNIVERSES);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(sanitizeUniverse).filter(Boolean).slice(0, MAX_UNIVERSES);
    } catch {
      return [];
    }
  }

  _save() {
    storageSet(STORAGE.UNIVERSES, JSON.stringify(this.universes));
  }

  getAll() {
    return this.universes.slice();
  }

  getLatest() {
    return this.universes[0] ?? null;
  }

  /**
   * @param {object} snapshot CosmosSystem.snapshot() output
   * @param {string} thumb PNG data URL
   * @returns {object|null} the stored entry
   */
  submit(snapshot, thumb) {
    const entry = sanitizeUniverse({ ...snapshot, thumb });
    if (!entry) return null;
    this.universes.unshift(entry);
    this.universes = this.universes.slice(0, MAX_UNIVERSES);
    this._save();
    return entry;
  }
}

function finite(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(min, Math.min(max, n));
}

function sanitizeUniverse(raw) {
  if (!raw || typeof raw !== 'object' || raw.v !== 2) return null;

  const name = String(raw.name ?? '').replace(/[^A-Z0-9 \-·]/gi, '').slice(0, 40);
  const era = ERAS.includes(raw.era) ? raw.era : ERAS[0];
  const hues = (Array.isArray(raw.hues) ? raw.hues : [])
    .slice(0, 4)
    .map((h) => finite(h, 0, 360))
    .filter((h) => h != null);
  const hueShift = finite(raw.hueShift, 0, 360) ?? 0;
  if (!name || hues.length !== 4) return null;

  const stars = (Array.isArray(raw.stars) ? raw.stars : [])
    .slice(0, MAX_SNAPSHOT_STARS)
    .map((s) => {
      if (!Array.isArray(s)) return null;
      const x = finite(s[0], -0.5, 1.5);
      const y = finite(s[1], -0.5, 1.5);
      const size = finite(s[2], 0.1, 10);
      const hue = finite(s[3], 0, 360);
      if (x == null || y == null || size == null || hue == null) return null;
      return [x, y, size, hue, s[4] === 1 ? 1 : 0];
    })
    .filter(Boolean);

  const nebulae = (Array.isArray(raw.nebulae) ? raw.nebulae : [])
    .slice(0, 12)
    .map((n) => {
      if (!Array.isArray(n)) return null;
      const x = finite(n[0], -0.5, 1.5);
      const y = finite(n[1], -0.5, 1.5);
      const r = finite(n[2], 0.01, 0.6);
      const hue = finite(n[3], 0, 360);
      if (x == null || y == null || r == null || hue == null) return null;
      return [x, y, r, hue];
    })
    .filter(Boolean);

  const links = (Array.isArray(raw.links) ? raw.links : [])
    .slice(0, 300)
    .map((l) => {
      const a = finite(l?.[0], 0, MAX_SNAPSHOT_STARS);
      const b = finite(l?.[1], 0, MAX_SNAPSHOT_STARS);
      if (a == null || b == null || !stars[Math.floor(a)] || !stars[Math.floor(b)]) return null;
      return [Math.floor(a), Math.floor(b)];
    })
    .filter(Boolean);

  const thumb =
    typeof raw.thumb === 'string' &&
    raw.thumb.startsWith('data:image/png') &&
    raw.thumb.length <= MAX_THUMB_LENGTH
      ? raw.thumb
      : '';

  return {
    v: 2,
    name,
    era,
    eraIndex: ERAS.indexOf(era),
    hues,
    hueShift,
    supernovae: finite(raw.supernovae, 0, 99) ?? 0,
    stars,
    nebulae,
    links,
    thumb,
    createdAt: finite(raw.createdAt, 0, 8.64e15) ?? Date.now()
  };
}
